"use client";

import React, { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Head from "../Base/Head";
import { motion } from "framer-motion";
import Footer from "../Base/Footer";
import ApiService from "../services/ApiService";
import { toast } from "react-hot-toast";
import { AddToCalendarButton } from "add-to-calendar-button-react";

import { generateICSFile, isPersonalEmail } from "../utils";

const customFieldLabels = {
  "glean-sydney" : {Email: "Official Email"}
}

const urlPattern =
  /(^|[^@\w])((?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+(?::\d+)?(?:\/[^\s<]*)?)/g;

const linkifyEmails = (text = "") =>
  text.replace(
    /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g,
    '<a href="mailto:$1">$1</a>'
  );

const linkifyUrls = (text = "") =>
  text.replace(urlPattern, (match, prefix, url) => {
    const href = url.startsWith("http://") || url.startsWith("https://")
      ? url
      : `https://${url}`;
    return `${prefix}<a href="${href}" target="_blank" rel="noreferrer">${url}</a>`;
  });

const formatRichText = (text = "") => linkifyUrls(linkifyEmails(text));

const CUSTOM_QUESTION_TYPES = {
  TEXT: "text",
  RADIO: "radio",
  CHECKBOX: "checkbox",
};

const getNormalizedCustomQuestions = (pageContent = {}) => {
  const customQuestionsRaw = Array.isArray(pageContent?.customQuestions)
    ? pageContent.customQuestions
    : [];

  const normalizedCustomQuestions = customQuestionsRaw
    .map((question, index) => {
      if (!question || typeof question !== "object") {
        return null;
      }
      const questionLabel = `${question.questionLabel || question.label || ""}`.trim();
      if (!questionLabel.length) {
        return null;
      }

      const answerTypeRaw = `${question.answerType || ""}`.toLowerCase().trim();
      const answerType = Object.values(CUSTOM_QUESTION_TYPES).includes(answerTypeRaw)
        ? answerTypeRaw
        : CUSTOM_QUESTION_TYPES.TEXT;

      const options = Array.isArray(question.options)
        ? question.options
            .map((option) => `${option || ""}`.trim())
            .filter((option) => option.length)
        : [];

      return {
        questionId: `${question.questionId || question.id || `custom-question-${index + 1}`}`.trim(),
        questionLabel,
        answerType,
        required: !!question.required,
        options:
          answerType === CUSTOM_QUESTION_TYPES.TEXT
            ? []
            : Array.from(new Set(options)),
      };
    })
    .filter(Boolean);

  if (normalizedCustomQuestions.length) {
    return normalizedCustomQuestions;
  }

  const legacyLabel = `${pageContent?.customInputLabel || ""}`.trim();
  if (!pageContent?.showCustomInput || !legacyLabel.length) {
    return [];
  }

  return [
    {
      questionId: "custom-question-1",
      questionLabel: legacyLabel,
      answerType: CUSTOM_QUESTION_TYPES.TEXT,
      required: !!pageContent?.customInputRequired,
      options: [],
    },
  ];
};

const FaqItem = ({
  question,
  answer,
  faqIndex,
  activeFaqIndex,
  setActiveFaqIndex,
  pageContent,
}) => {
  const isFaqSelected = activeFaqIndex === faqIndex;
  const isFirstFaq = faqIndex === 0;

  const handleFaqClick = () => {
    setActiveFaqIndex(isFaqSelected ? null : faqIndex);
  };

  // Replace email addresses with mailto links
  const formattedAnswer = formatRichText(answer);

  return (
    <div
      className={`faq ${isFirstFaq ? "first" : ""}`}
      style={{
        "--theme-primary-color": pageContent.themePrimaryColor || "#015637",
      }}
    >
      <div
        onClick={handleFaqClick}
        className={`faq-head ${isFaqSelected ? "active" : ""}`}
      >
        <div>{question}</div>
      </div>
      <div className={`faq-body ${isFaqSelected ? "active" : ""}`}>
        <p dangerouslySetInnerHTML={{ __html: formattedAnswer }} />
      </div>
    </div>
  );
};

const RsvpField = ({
  className = "",
  fieldLabel,
  inputType = "text",
  value,
  setValue,
  required,
  onBlur = () => {},
}) => {
  const [isValid, setIsValid] = useState(false);
  useEffect(() => {
    if (!required) {
      setIsValid(true);
    } else if (required && `${value}`.trim().length) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [value]);

  return (
    <div className={`rsvp-field ${className}`}>
      <div className="rsvp-field-label">
        {fieldLabel}
        {required ? <span className="asterisk">*</span> : null}
      </div>
      {inputType !== "textarea" ? (
        <input
          type={inputType}
          className="form-control"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onBlur={onBlur}
        />
      ) : (
        <textarea
          rows="2"
          className="form-control"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onBlur={onBlur}
        ></textarea>
      )}

      {/* {!isValid ? (
        <div className="required-field-msg">Required field</div>
      ) : null} */}
    </div>
  );
};

const CustomQuestionField = ({
  question,
  value,
  className = "",
  onTextChange,
  onRadioChange,
  onCheckboxChange,
}) => {
  const { questionId, questionLabel, answerType, required, options } = question;
  const showOptions =
    answerType !== CUSTOM_QUESTION_TYPES.TEXT && Array.isArray(options) && options.length;

  return (
    <div className={`rsvp-field ${className}`}>
      <div className="rsvp-field-label">
        {questionLabel}
        {required ? <span className="asterisk">*</span> : null}
      </div>
      {!showOptions ? (
        <input
          type="text"
          className="form-control"
          value={`${value || ""}`}
          onChange={(event) => onTextChange(questionId, event.target.value)}
        />
      ) : (
        <div className="custom-question-options">
          {options.map((option, optionIndex) => {
            const optionInputId = `${questionId}-option-${optionIndex}`;
            if (answerType === CUSTOM_QUESTION_TYPES.CHECKBOX) {
              const selectedOptions = Array.isArray(value) ? value : [];
              return (
                <label
                  key={optionInputId}
                  htmlFor={optionInputId}
                  className="form-check custom-question-option"
                >
                  <input
                    id={optionInputId}
                    type="checkbox"
                    className="form-check-input"
                    checked={selectedOptions.includes(option)}
                    onChange={(event) =>
                      onCheckboxChange(questionId, option, event.target.checked)
                    }
                  />
                  <div className="form-check-label custom-question-option-label">
                    {option}
                  </div>
                </label>
              );
            }

            return (
              <label
                key={optionInputId}
                htmlFor={optionInputId}
                className="form-check custom-question-option"
              >
                <input
                  id={optionInputId}
                  type="radio"
                  name={`custom-question-${questionId}`}
                  className="form-check-input"
                  checked={`${value || ""}` === option}
                  onChange={() => onRadioChange(questionId, option)}
                />
                <div className="form-check-label custom-question-option-label">
                  {option}
                </div>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

const EventDetails = ({
  motionTransisition,
  motionVariants,
  pageContent,
  handleRsvpBtnClicked,
  registrationClosed,
  rsvpButtonText,
}) => {
  return (
    <motion.div
      className="ed-event-info"
      initial="initial"
      animate="animate"
      transition={motionTransisition}
      variants={motionVariants}
    >
      <div className="event-inner">
        <div className="event-image">
          <img className="img-fluid" src={pageContent.eventImages[0]} />
        </div>
        <div className="event-info-container">
          <div className="event-details-heading">EVENT DETAILS</div>
          <div className="event-info">
            <div className="event-info-text">
              <span className="start">Location:</span>
              <br />
              <p>{pageContent.eventLocationFull}</p>
            </div>
            {pageContent?.eventSpeaker?.trim() ? (
              <div className="event-info-text">
                <span className="start">Speaker:</span>
                {pageContent.eventSpeaker.trim()}
              </div>
            ) : null}
            <div className="event-info-text">
              <span className="start">Date:</span>
              {pageContent.eventDate}
            </div>
            <div className="event-info-text">
              <span className="start">Time:</span>
              {pageContent.eventTime}
            </div>
          </div>
          {registrationClosed ? (
            <div className="reg-closed-small">Registration is closed</div>
          ) : (
            <div
              className="rsvp-button"
              style={{
                "--theme-primary-color":
                  pageContent.themePrimaryColor || `#61999d`,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.filter = "brightness(93%)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.filter = "brightness(100%)")
              }
              onClick={handleRsvpBtnClicked}
            >
              {rsvpButtonText}
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const SpeakerBioCard = ({
  motionTransisition,
  motionVariants,
  showSpeakerProfile = false,
}) => {
  if (!showSpeakerProfile) {
    return null;
  }
  return (
    <motion.div
      className="ed-event-info ed-speaker-info"
      initial="initial"
      animate="animate"
      transition={motionTransisition}
      variants={motionVariants}
    >
      <div className="speaker-profile">
        <div className="speaker-heading">Thought Leader</div>
        <div className="speaker-image">
          <img
            className="img-fluid"
            src={process.env.PUBLIC_URL + "/images/dynamic/dave-kellermanns.png"}
            alt="Dave Kellermanns"
          />
        </div>
        <div className="speaker-name">Dave Kellermanns</div>
        <div className="speaker-title">Global Advisor for Workload at Broadcom</div>
        <div className="speaker-bio">
          Dave Kellermanns is a Global Advisor for Workload at Broadcom. In the
          past he has worked with a variety of Fortune 100 companies to review
          and strategize around their automation strategy and start innovating
          for the benefit of the business. As companies need to automate at a
          rapid pace in order to innovate, in his role as Advisor he provides a
          bigger picture on automation, addressing silos and seeing if manual
          processes exist that could or should be automated and ultimately
          freeing up teams to better use their skills and to develop them
          further for the benefit of the business.
        </div>
      </div>
      <div className="speaker-profile speaker-profile--secondary">
        <div className="speaker-heading">Fireside Chat</div>
        <div className="speaker-image">
          <img
            className="img-fluid"
            src={process.env.PUBLIC_URL + "/images/dynamic/michael-woods.png"}
            alt="Michael Woods"
          />
        </div>
        <div className="speaker-name">Michael Woods</div>
        <div className="speaker-title">
          Product Manager for Automation at Broadcom
        </div>
        <div className="speaker-bio">
          Michael Woods will participate in the Fireside Chat to layer in
          real-world examples of customer experiences.
        </div>
      </div>
    </motion.div>
  );
};

const PartnerHeaderLogos = ({ headerLogos, className = "" }) => {
  const maxWidth = 156; //.ed-wrapper .header-bar .header-container .header-logos .header-logo
  const scaleMaxWidth = (scale) => {
    if (scale === 100) {
      return maxWidth;
    } else {
      // Calculate the width based on the scale and round to the nearest pixel
      return Math.round((scale / 100) * maxWidth);
    }
  };
  return (
    <>
      {/* <div className={`header-logo ${className}`}>
        <img
          className="img-fluid"
          src={
            process.env.PUBLIC_URL + "/images/dynamic/google-cloud-logo.png"
          }
          alt="Google Cloud"
        />
      </div> */}
      {headerLogos.map((hl, i) => {
        const style = { maxWidth: `${scaleMaxWidth(hl.scale)}px` };
        if (hl.yOffset && hl.yOffset !== 0) {
          style.transform = `translateY(${hl.yOffset}px)`;
        }
        return (
          <div key={i} className={`header-logo ${className}`} style={style}>
            <img className="img-fluid" src={hl.src} />
          </div>
        );
      })}
    </>
  );
};

export default function DynamicEvent({
  event = {},
  referral = {},
  activePageUrl = "",
}) {
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);
  const [rsvpModal, setRsvpModal] = useState(false);
  const [modalShowClass, setModalShowClass] = useState("");
  const [isFooterVisible, setIsFooterVisible] = useState(true);

  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [dietaryRestrictions, setDietaryRestrictions] = useState("");
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [dataAgreement, setDataAgreement] = useState(true);

  const [customQuestionAnswers, setCustomQuestionAnswers] = useState({});

  const [rsvpSubmitted, setRsvpSubmitted] = useState(false);
  const [apolloEmailChecked, setApolloEmailChecked] = useState(""); // the last email apollo checked

  const { pageContent, registrationClosed, calendarOptions } = event;
  const customQuestions = useMemo(
    () => getNormalizedCustomQuestions(pageContent),
    [pageContent]
  );
  const hasCustomQuestions = customQuestions.length > 0;

  const textClass = pageContent?.textClass || "";
  const headerTextClass = pageContent?.headerTextClass || "";

  useEffect(() => {
    document.title = pageContent.heroHeading;
  }, []);

  useEffect(() => {
    setCustomQuestionAnswers((previousAnswers) => {
      const nextAnswers = {};
      for (const question of customQuestions) {
        const previousAnswer = previousAnswers[question.questionId];
        if (question.answerType === CUSTOM_QUESTION_TYPES.CHECKBOX) {
          nextAnswers[question.questionId] = Array.isArray(previousAnswer)
            ? previousAnswer
            : [];
        } else {
          nextAnswers[question.questionId] =
            typeof previousAnswer === "string" ? previousAnswer : "";
        }
      }
      return nextAnswers;
    });
  }, [customQuestions]);

  const marketingOptInChange = (e) => {
    if (e.target.checked) {
      setMarketingOptIn(true);
    } else {
      setMarketingOptIn(false);
    }
  };

  const dataAgreementChange = (e) => {
    if (e.target.checked) {
      setDataAgreement(true);
    } else {
      setDataAgreement(false);
    }
  };

  const handleCustomQuestionTextChange = (questionId, value) => {
    setCustomQuestionAnswers((previousAnswers) => ({
      ...previousAnswers,
      [questionId]: value,
    }));
  };

  const handleCustomQuestionRadioChange = (questionId, option) => {
    setCustomQuestionAnswers((previousAnswers) => ({
      ...previousAnswers,
      [questionId]: option,
    }));
  };

  const handleCustomQuestionCheckboxChange = (questionId, option, checked) => {
    setCustomQuestionAnswers((previousAnswers) => {
      const existingAnswers = Array.isArray(previousAnswers[questionId])
        ? previousAnswers[questionId]
        : [];
      const updatedAnswers = checked
        ? Array.from(new Set([...existingAnswers, option]))
        : existingAnswers.filter((existingOption) => existingOption !== option);
      return {
        ...previousAnswers,
        [questionId]: updatedAnswers,
      };
    });
  };

  const titleFormattedHeroHeading = pageContent.heroHeading
    .replace(/\\n/g, " - ") // Replace escaped newlines
    .replace(/\n/g, " - "); // Replace unescaped newlines

  const head = {
    title: titleFormattedHeroHeading,
    description: pageContent.heroSubHeading,
    url: `https://events.landmarkventures.com/${event.pageUrl}`,
    image:
      pageContent?.twitterCardImage ||
      pageContent?.heroImages?.[0] ||
      pageContent?.eventImages?.[0] ||
      event.imageUrl ||
      "",
  };

  async function handleSetRsvpModal(show) {
    if (show) {
      setIsFooterVisible(false);
      setRsvpModal(true);
    } else {
      setIsFooterVisible(true);
      setRsvpModal(false);
    }
  }

  async function handleRsvpBtnClicked() {
    const redirectUrl = `${pageContent?.rsvpButtonUrl || ""}`.trim();
    if (pageContent?.rsvpButtonUrlRedirect && redirectUrl.length) {
      const normalizedRedirectUrl = /^https?:\/\//i.test(redirectUrl)
        ? redirectUrl
        : `https://${redirectUrl}`;
      window.location.href = normalizedRedirectUrl;
      return;
    }
    setIsFooterVisible(false);
    setRsvpModal(true);
    // setTimeout(() => {
    // }, 0);
  }

  const subtleBounceTransition = {
    type: "spring",
    stiffness: 96, // Lower stiffness for a more subtle bounce
    damping: 28, // Higher damping to reduce the bounce intensity
    duration: 2, // Increase the duration for a slower overall effect
  };

  const scaleInTransition = {
    type: "spring",
    stiffness: 200, // Lower stiffness for a more subtle bounce
    damping: 100, // Higher damping to reduce the bounce intensity
    duration: 0.7, // Increase the duration for a slower overall effect
  };

  const headerVariants = {
    initial: { y: -140, opacity: 0 }, // Start off-screen to the left with no opacity and no scale
    animate: { y: 0, opacity: 1 }, // Move to the final position, full opacity, and scale
  };

  const infoVariants = {
    initial: { x: -700, opacity: 0 }, // Start off-screen to the left with no opacity and no scale
    animate: { x: 0, opacity: 1 }, // Move to the final position, full opacity, and scale
  };

  const imageVariants = {
    initial: { x: 700, opacity: 0 }, // Start off-screen to the left with no opacity and no scale
    animate: { x: 0, opacity: 1 }, // Move to the final position, full opacity, and scale
  };

  const scaleVariants = {
    initial: { scale: 0, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
  };

  let formValid = false;
  let formErrorMsg = ``;
  // validate form
  const validateForm = () => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!email.length || !emailRegex.test(email)) {
      formErrorMsg = "Please enter a valid email address";
      return false;
    }
    if(pageContent.businessEmailsOnly && isPersonalEmail(email)) {
      formErrorMsg = "Please use your business email address";
      return false;
    }
    if (!firstName.length) {
      formErrorMsg = "Please enter your first name";
      return false;
    }
    if (!lastName.length) {
      formErrorMsg = "Please enter your last name";
      return false;
    }
    if (!jobTitle.length) {
      formErrorMsg = "Please enter your job title";
      return false;
    }
    if (!company.length) {
      formErrorMsg = "Please enter your company";
      return false;
    }

    for (const question of customQuestions) {
      if (!question.required) {
        continue;
      }
      const currentValue = customQuestionAnswers[question.questionId];
      if (question.answerType === CUSTOM_QUESTION_TYPES.CHECKBOX) {
        if (!Array.isArray(currentValue) || !currentValue.length) {
          formErrorMsg = `Please answer the ${question.questionLabel} question`;
          return false;
        }
      } else if (!`${currentValue || ""}`.trim().length) {
        formErrorMsg = `Please answer the ${question.questionLabel} question`;
        return false;
      }
    }

    // if (!marketingOptIn) {
    //   formErrorMsg =
    //     "You must agree to receive news from Google and its Partners";
    //   return false;
    // }
    // if (!dataAgreement) {
    //   formErrorMsg = "You must confirm you understand the data privacy policy";
    //   return false;
    // }
    return true;
  };
  formValid = validateForm();
  const submitDisabledClass = formValid ? "" : "disabled";

  async function handleEmailBlur() {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!email.length || !emailRegex.test(email)) {
      // invalid email address
      return false;
    }
    // email valid

    // email already checked by apollo
    if (
      apolloEmailChecked.toLowerCase().trim() === email.toLowerCase().trim()
    ) {
      return;
    }

    // autofill is enabled for this event
    if (event.apolloAutofill) {
      // search email on apollo
      try {
        setApolloEmailChecked(email);
        const api = new ApiService();
        const response = await api.get(
          `landmark/apollo/searchByEmail/${email.trim()}`
        );
        // console.log(`handleEmailBlur() - response`, response);
        if (response.success && response.data) {
          const { first_name, last_name, organization_name, title } =
            response.data;
          // autofill form
          setCompany(organization_name);
          setJobTitle(title);
          setFirstName(first_name);
          setLastName(last_name);
        } else {
          throw "Email search error";
        }
      } catch (error) {
        console.error("handleEmailBlur() - Error searching email", error);
      }
    }
  }

  async function submitRsvp() {
    if (!formValid) {
      console.log(`submitRsvp() - form is invalid`);
      return;
    }
    const form = {
      eventId: event.eventId,
      pageUrl: event.pageUrl,
      firstName,
      lastName,
      company,
      jobTitle,
      email,
      dietaryRestrictions,
      marketingOptIn,
    };

    const formattedCustomQuestionAnswers = customQuestions
      .map((question) => {
        const response = customQuestionAnswers[question.questionId];
        if (question.answerType === CUSTOM_QUESTION_TYPES.CHECKBOX) {
          const answers = Array.isArray(response)
            ? response
                .map((value) => `${value || ""}`.trim())
                .filter((value) => value.length)
            : [];
          if (!answers.length) {
            return null;
          }
          return {
            questionId: question.questionId,
            questionLabel: question.questionLabel,
            answerType: question.answerType,
            answer: answers.join(", "),
            answers,
          };
        }

        const answer = `${response || ""}`.trim();
        if (!answer.length) {
          return null;
        }
        return {
          questionId: question.questionId,
          questionLabel: question.questionLabel,
          answerType: question.answerType,
          answer,
          answers: [answer],
        };
      })
      .filter(Boolean);

    if (formattedCustomQuestionAnswers.length) {
      form.customQuestionAnswers = formattedCustomQuestionAnswers;
      // Keep legacy keys for backward compatibility.
      form.customQuestion = formattedCustomQuestionAnswers[0].questionLabel;
      form.customAnswer = formattedCustomQuestionAnswers[0].answer;
    }

    if (referral && referral.url && referral.name) {
      form.referredByUrl = referral.url;
      form.referredByName = referral.name;
    }

    console.log(`submitRsvp()`, form);
    try {
      const api = new ApiService();
      const response = await api.post(`landmark/dynamicEvents/submitRsvp`, {
        form,
      });
      console.log(`submitRsvp() - response`, response);
      if (response.success) {
        setTimeout(() => {
          setRsvpSubmitted(true);
        }, 50);
      } else {
        throw "Backend error";
      }
    } catch (error) {
      console.error("submitRsvp() - Error submitting form", error);
      toast.error("An error occurred. Please try again later.");
    }
  }

  let formattedHeroHeading = formatRichText(pageContent.heroHeading || "")
    .replace(/\\n/g, "<br /><span>") // Replace escaped newlines
    .replace(/\n/g, "<br /><span>"); // Replace unescaped newlines
  // Add closing </span> only if a <span> was added
  if (formattedHeroHeading.includes("<br /><span>")) {
    formattedHeroHeading += "</span>";
  }

  const formattedHeroSubHeading = formatRichText(
    pageContent.heroSubHeading || ""
  );

  const formattedHeroText = formatRichText(pageContent.heroText || "")
    .replace(/\\n/g, "<br />")
    .replace(/\n/g, "<br />");

  const emailFieldLabel = customFieldLabels[event.pageUrl]?.Email || "Business Email";
  const rsvpButtonText = pageContent?.rsvpButtonText?.trim() || "RSVP";
  const registerButtonText = pageContent?.registerButtonText?.trim() || "Register";
  const showBroadcomSpeakerProfile =
    activePageUrl.toLowerCase().trim() === "broadcom-chicago-2026";

  return (
    <>
      <Head head={head} />
      <div className={`ed-wrapper ${textClass}`}>
        <div className="ed-main">
          <motion.div
            className="header-bar"
            initial="initial"
            animate="animate"
            transition={subtleBounceTransition}
            variants={headerVariants}
          >
            <div className="auto__container">
              <div className="header-container">
                <div className="header-logos left">
                  <PartnerHeaderLogos headerLogos={pageContent.headerLogos} />
                  {!pageContent.hideLandmarkHeaderLogo ? (
                    <div className="header-logo lg mob-only">
                      <img
                        className="img-fluid "
                        src={
                          process.env.PUBLIC_URL +
                          "/images/dynamic/landmark-logo-alt.png"
                        }
                        alt="Landmark Ventures"
                      />
                    </div>
                  ) : null}
                </div>
                <div className="header-logos right">
                  {/* <PartnerHeaderLogos headerLogos={pageContent.headerLogos} /> */}
                  {!pageContent.hideLandmarkHeaderLogo ? (
                    <div className="header-logo lg">
                      <img
                        className="img-fluid"
                        src={
                          process.env.PUBLIC_URL +
                          "/images/dynamic/landmark-logo-alt.png"
                        }
                        alt="Landmark Ventures"
                      />
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </motion.div>

          <div className="auto__container">
            <div className="ed-block-container">
              <div className="ed-vertical-flex">
                <div className="ed-left-container">
                  <div className="ed-hero">
                    <motion.div
                      className="hero-info-container"
                      initial="initial"
                      animate="animate"
                      transition={subtleBounceTransition}
                      variants={infoVariants}
                    >
                      <div className="hero-location">
                        <img
                          src={
                            process.env.PUBLIC_URL +
                            "/images/dynamic/location-pointer.svg"
                          }
                          loading="lazy"
                          alt=""
                        />
                        <div>{pageContent.eventLocationShort}</div>
                      </div>
                      <p
                        className={`hero-heading section-heading ${headerTextClass}`}
                        dangerouslySetInnerHTML={{
                          __html: formattedHeroHeading,
                        }}
                      />
                      {registrationClosed ? (
                        <div className="reg-closed-big-container">
                          <div className="reg-closed-divider top"></div>
                          <div className="reg-closed-big">
                            Registration is now closed. Thank you for your
                            interest!
                          </div>
                          <div className="reg-closed-divider bottom"></div>
                        </div>
                      ) : null}
                      <div
                        className="hero-subheading"
                        dangerouslySetInnerHTML={{
                          __html: formattedHeroSubHeading,
                        }}
                      />
                      <div
                        className="hero-text"
                        dangerouslySetInnerHTML={{
                          __html: formattedHeroText,
                        }}
                      />
                      {pageContent.expectBullets &&
                      pageContent.expectBullets.length ? (
                        <ul className="expect-bullets">
                          {pageContent.expectBullets.map((b, i) => (
                            <li key={i}>{b}</li>
                          ))}
                        </ul>
                      ) : null}
                      {registrationClosed ? (
                        <div className="reg-closed-small">
                          Registration is closed
                        </div>
                      ) : (
                        <>
                          <div
                            className="rsvp-button"
                            style={{
                              "--theme-primary-color":
                                pageContent.themePrimaryColor || `#61999d`,
                            }}
                            onMouseEnter={(e) =>
                              (e.currentTarget.style.filter = "brightness(93%)")
                            }
                            onMouseLeave={(e) =>
                              (e.currentTarget.style.filter =
                                "brightness(100%)")
                            }
                            onClick={handleRsvpBtnClicked}
                          >
                            {rsvpButtonText}
                          </div>
                          <div className="expect-disclaimer">
                            {pageContent.expectText}
                          </div>
                        </>
                      )}
                    </motion.div>

                    {/* <motion.div
                className="hero-image-container"
                initial="initial"
                animate="animate"
                transition={subtleBounceTransition}
                variants={imageVariants}
              >
                <div className="hero-image">
                  <img className="img-fluid" src={pageContent.heroImages[0]} />
                </div>
              </motion.div> */}
                  </div>
                  {/* <div className="horizontal-rule hero"></div> */}
                  <div className="w-100 mob-only">
                  <EventDetails
                    motionTransisition={subtleBounceTransition}
                    motionVariants={imageVariants}
                    pageContent={pageContent}
                    handleRsvpBtnClicked={handleRsvpBtnClicked}
                    registrationClosed={registrationClosed}
                    rsvpButtonText={rsvpButtonText}
                  />
                  <SpeakerBioCard
                    motionTransisition={subtleBounceTransition}
                    motionVariants={imageVariants}
                    showSpeakerProfile={showBroadcomSpeakerProfile}
                  />
                  </div>

                  <motion.div
                    className="ed-expect"
                    initial="initial"
                    animate="animate"
                    transition={{ ...subtleBounceTransition, delay: 0.2 }}
                    variants={infoVariants}
                  >
                    {/* <div className="expect-title section-heading sm">
                      What to Expect
                    </div>
                    <div className="expect-text">{pageContent.expectText}</div>
                    {pageContent.expectBullets &&
                    pageContent.expectBullets.length ? (
                      <ul className="expect-bullets">
                        {pageContent.expectBullets.map((b, i) => (
                          <li key={i}>{b}</li>
                        ))}
                      </ul>
                    ) : null}
                    <div className="expect-disclaimer">
                      Space is limited. Please register ASAP and we will send
                      along a calendar invite!
                    </div> */}

                    <div className="horizontal-rule"></div>

                    {pageContent.faqs && pageContent.faqs.length ? (
                      <div className="ed-faqs">
                        <div className="ed-faq-title section-heading sm">
                          FAQs
                        </div>
                        {pageContent.faqs.map((faq, i) => (
                          <FaqItem
                            key={i}
                            question={faq.question}
                            answer={faq.answer}
                            faqIndex={i}
                            activeFaqIndex={activeFaqIndex}
                            setActiveFaqIndex={setActiveFaqIndex}
                            pageContent={pageContent}
                          />
                        ))}
                      </div>
                    ) : null}
                  </motion.div>

                  {/* <div className="footer-spacer"></div> */}
                </div>
                <div className="ed-right-rail">
                  <div className="sticky-content">
                    <EventDetails
                      motionTransisition={subtleBounceTransition}
                      motionVariants={imageVariants}
                      pageContent={pageContent}
                      handleRsvpBtnClicked={handleRsvpBtnClicked}
                      registrationClosed={registrationClosed}
                      rsvpButtonText={rsvpButtonText}
                    />
                    <SpeakerBioCard
                      motionTransisition={subtleBounceTransition}
                      motionVariants={imageVariants}
                      showSpeakerProfile={showBroadcomSpeakerProfile}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {pageContent.footerImages && pageContent.footerImages.length ? (
            <div className="auto__container">
              <div className="footer-image-outer">
                <div className="footer-image">
                  <img
                    className="img-fluid"
                    src={pageContent.footerImages[0]}
                  />
                </div>
              </div>
            </div>
          ) : null}
          <div className="footer-spacer"></div>

          {/* optional hidelandmark footer */}
          {!pageContent.hideLandmarkFooter ? (
            <div className="ed-footer">
              <Footer alternate={true} />
            </div>
          ) : null}
        </div>
      </div>
      <Modal
        className={`modal fade rsvp-modal ${modalShowClass}`}
        style={{
          "--theme-primary-color": pageContent?.themePrimaryColor || "#61999d",
        }}
        centered
        show={rsvpModal}
        onHide={() => {
          setModalShowClass("scale-out");
          handleSetRsvpModal(false);
          // setTimeout(() => , 300);
        }}
        onShow={() => {
          setModalShowClass("scale-in");
        }}
        dialogClassName="scale-in"
      >
        {rsvpSubmitted ? (
          <>
            <div
              className="rsvp-modal-close"
              onClick={() => handleSetRsvpModal(false)}
            >
              X
            </div>
            <motion.div
              initial="initial"
              animate="animate"
              transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
              variants={scaleVariants}
            >
              <div className="rsvp-modal-title sm">Thank you for your RSVP</div>
              <div className="rsvp-submitted-text">
                {pageContent.afterSubmittedText}
              </div>
              {calendarOptions &&
              calendarOptions.showAddToCalendarButton &&
              calendarOptions.eventProkey ? (
                <div className="add-to-cal-button-lib">
                  {/* <AddToCalendarButton
                    name="Test-Event"
                    startDate="2023-05-22"
                    options={["Apple", "Google", "Yahoo", "iCal"]}
                    timeZone="America/Los_Angeles"
                  ></AddToCalendarButton> */}
                  <AddToCalendarButton
                    proKey={calendarOptions.eventProkey}
                  ></AddToCalendarButton>
                </div>
              ) : null}
              {/* {calendarOptions && calendarOptions.showAddToCalendarButton ? (
                <div
                  className="add-to-cal-button"
                  onClick={() => {
                    generateICSFile(calendarOptions);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                    width="20"
                    height="20"
                    style={{ marginRight: "8px", verticalAlign: "middle" }}
                  >
                    <path d="M128 0c17.7 0 32 14.3 32 32l0 32 128 0 0-32c0-17.7 14.3-32 32-32s32 14.3 32 32l0 32 48 0c26.5 0 48 21.5 48 48l0 48L0 160l0-48C0 85.5 21.5 64 48 64l48 0 0-32c0-17.7 14.3-32 32-32zM0 192l448 0 0 272c0 26.5-21.5 48-48 48L48 512c-26.5 0-48-21.5-48-48L0 192zM305 305c9.4-9.4 9.4-24.6 0-33.9s-24.6-9.4-33.9 0l-47 47-47-47c-9.4-9.4-24.6-9.4-33.9 0s-9.4 24.6 0 33.9l47 47-47 47c-9.4 9.4-9.4 24.6 0 33.9s24.6 9.4 33.9 0l47-47 47 47c9.4 9.4 24.6 9.4 33.9 0s9.4-24.6 0-33.9l-47-47 47-47z" />
                  </svg>
                  Add to Calendar
                </div>
              ) : null} */}
            </motion.div>{" "}
          </>
        ) : (
          <>
            <div className="rsvp-modal-title">Register for the event</div>
            <div
              className="rsvp-modal-close"
              onClick={() => handleSetRsvpModal(false)}
            >
              X
            </div>
            <div className="fields-column">
              <RsvpField
                className="first"
                fieldLabel={emailFieldLabel}
                inputType="email"
                value={email}
                setValue={setEmail}
                required={true}
                onBlur={handleEmailBlur}
              />
              <div className="fields-row">
                <RsvpField
                  fieldLabel="First Name"
                  value={firstName}
                  setValue={setFirstName}
                  required={true}
                  halfWidth={true}
                />
                <RsvpField
                  fieldLabel="Last Name"
                  value={lastName}
                  setValue={setLastName}
                  required={true}
                  halfWidth={true}
                />
              </div>
              <RsvpField
                fieldLabel="Job Title"
                value={jobTitle}
                setValue={setJobTitle}
                required={true}
              />
              <RsvpField
                className={`${
                  !hasCustomQuestions && pageContent.hideDietaryInput
                    ? "last"
                    : ""
                }`}
                fieldLabel="Company Name"
                value={company}
                setValue={setCompany}
                required={true}
              />
              {customQuestions.map((question, index) => {
                const isLastCustomQuestion =
                  index === customQuestions.length - 1 && pageContent.hideDietaryInput;
                return (
                  <CustomQuestionField
                    key={question.questionId}
                    className={isLastCustomQuestion ? "last" : ""}
                    question={question}
                    value={customQuestionAnswers[question.questionId]}
                    onTextChange={handleCustomQuestionTextChange}
                    onRadioChange={handleCustomQuestionRadioChange}
                    onCheckboxChange={handleCustomQuestionCheckboxChange}
                  />
                );
              })}
              {pageContent.hideDietaryInput ? null : (
                <RsvpField
                  className="last"
                  fieldLabel="Dietary Restrictions"
                  inputType="textarea"
                  value={dietaryRestrictions}
                  setValue={setDietaryRestrictions}
                />
              )}

              {event.googlePolicyOptions ? (
                <label
                  htmlFor="marketingOptIn"
                  className="check rsvp-checkbox-container"
                >
                  <div className="check__box">
                    <input
                      type="checkbox"
                      id="marketingOptIn"
                      checked={marketingOptIn}
                      onChange={marketingOptInChange}
                    />
                    <span>
                      <img
                        src={
                          process.env.PUBLIC_URL +
                          "/images/icons/tick-white.svg"
                        }
                        alt="icon"
                      />
                    </span>
                  </div>
                  <div className="check-label">
                    Sign me up to receive news, product updates, event
                    information and special offers from Google Cloud and its
                    Partners. (optional)
                  </div>
                </label>
              ) : null}
              {event.rubrikPolicyOptions ? (
                <label
                  htmlFor="marketingOptIn"
                  className="check rsvp-checkbox-container"
                >
                  <div className="check__box">
                    <input
                      type="checkbox"
                      id="marketingOptIn"
                      checked={marketingOptIn}
                      onChange={marketingOptInChange}
                    />
                    <span>
                      <img
                        src={
                          process.env.PUBLIC_URL +
                          "/images/icons/tick-white.svg"
                        }
                        alt="icon"
                      />
                    </span>
                  </div>
                  <div className="check-label">
                    Sign me up to receive news, product updates, event
                    information and special offers from Rubrik. (optional)
                  </div>
                </label>
              ) : null}
            </div>

            <div
              className="register-button"
              onClick={() => submitRsvp()}
              style={{
                "--theme-primary-color":
                  pageContent.themePrimaryColor || `#61999d`,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.filter = "brightness(93%)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.filter = "brightness(100%)")
              }
            >
              {registerButtonText}
            </div>
            {!formValid ? (
              <div className="required-field-msg bottom">{formErrorMsg}</div>
            ) : (
              <div className="required-field-msg bottom"></div>
            )}
            {event.googlePolicyOptions ? (
              <label
                htmlFor="dataAgreement"
                className="check rsvp-checkbox-container"
              >
               <div className="check-label legal-notice">
  I understand my personal data will be processed by Google in accordance with{" "}
  <a
    className="inline-link"
    href="https://policies.google.com/privacy"
    target="_blank"
  >
    Google’s Privacy Policy
  </a>{" "}
          {event.pageUrl.toLowerCase().includes("mistral") ? (
            <>
              and{" "}
              <a
                className="inline-link"
                href="https://mistral.ai/terms#privacy-policy"
                target="_blank"
              >
                Mistral's Privacy Policy
              </a>{" "}
            </>
          ) : null}
          and may be shared with members of{" "}
          <a
            className="inline-link"
            href="https://cloud.google.com/partners"
            target="_blank"
          >
            Google Cloud’s Partner Program
          </a>
          , who may send you marketing communications in accordance with the partner's
          privacy policy.
        </div>
              </label>
            ) : null}
            {event.rubrikPolicyOptions ? (
              <label
                htmlFor="dataAgreement"
                className="check rsvp-checkbox-container"
              >
                <div className="check-label legal-notice">
                  I understand my personal data will be processed by Rubrik in accordance with{" "}
                  <a
                    className="inline-link"
                    href="https://www.rubrik.com/legal/privacy-policy"
                    target="_blank"
                  >
                    Rubrik's Privacy Policy
                  </a>{" "}.
                </div>
              </label>
            ) : null}
          </>
        )}
      </Modal>
    </>
  );
}
